import React, { useMemo } from 'react'
import { FeatureFlag } from '@openfeature/react-sdk'
import { Link, useLocation } from '@tanstack/react-router'
import {
	ChartPieIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	CircleDollarSignIcon,
	DatabaseIcon,
	DownloadIcon,
	HeadsetIcon,
	PuzzleIcon,
	RouteIcon,
	Share2Icon,
	TrendingUpDownIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from 'usehooks-ts'

import { AssistantChat } from '@/features/ai-assistant'
import { CommandMenu } from '@/features/command'

import { useExportsQuery } from '@/entities/data-export'
import { Access, useAccess } from '@/entities/session'

import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '@/shared/components/ui/hover-card'
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuBadge,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarRail,
	SidebarSeparator,
	useSidebar,
} from '@/shared/components/ui/sidebar'
import { useModals } from '@/shared/lib/modal-manager'

import AppSidebarFooter from './AppSidebarFooter'
import AppSidebarHeader from './AppSidebarHeader'
import AppSidebarIntegrationMenuItem from './AppSidebarIntegrationMenuItem'

const useMenuItems = () => {
	const { checkPermissons } = useAccess()
	const { t } = useTranslation()

	return useMemo(
		() => [
			{
				title: t('menu.general_overview', {
					defaultValue: 'General overview',
				}),
				icon: <ChartPieIcon />,
				enabled: checkPermissons(['ACCESS_BI']),
				items: [
					{
						title: t('main_dashboard_title', {
							defaultValue: 'Main dashboard',
						}),
						pathname: '/p/dashboard/main',
						enabled: true,
					},
					{
						title: t('ai_dashboard_title', {
							defaultValue: 'AI dashboard',
						}),
						pathname: '/p/dashboard/ai',
						enabled: true,
					},
					{
						title: t('saved_reports.page.title', {
							defaultValue: 'Saved reports',
						}),
						pathname: '/p/dashboard/reports',
						enabled: true,
					},
				],
			},
			{
				title: t('menu.pricing', {
					defaultValue: 'Pricing',
				}),
				icon: <CircleDollarSignIcon />,
				items: [
					{
						title: t('menu.og', {
							defaultValue: 'Optimization groups',
						}),
						pathname: '/p/og',
						enabled: true,
					},
					{
						title: t('menu.pricing_campaigns', {
							defaultValue: 'Pricing campaigns',
						}),
						pathname: '/p/pc',
						enabled: true,
					},
					{
						title: t('menu.promo', {
							defaultValue: 'Promo',
						}),
						pathname: '/p/promo',
						enabled: true,
					},
					{
						title: t('app.products', {
							defaultValue: 'Products',
						}),
						pathname: '/p/products',
						enabled: true,
					},
				],
			},

			{
				title: t('menu.anchor_management', {
					defaultValue: 'Anchor management',
				}),
				icon: <Share2Icon className='size-4' />,
				pathname: '/p/anchor-management',
				enabled: true,
			},
			{
				title: t('templates', {
					defaultValue: 'Templates',
				}),
				pathname: '/p/templates',
				icon: <PuzzleIcon />,
				enabled: true,
			},
			{
				title: t('whatif', {
					defaultValue: 'What-if',
				}),
				pathname: '/p/whatif',
				icon: <TrendingUpDownIcon />,
				enabled: true,
			},
		],
		[checkPermissons, t]
	)
}

export default function AppSidebar() {
	const { t } = useTranslation()
	const location = useLocation()

	const { showModal } = useModals()
	const { open } = useSidebar()

	const pricngItems = useMenuItems()

	return (
		<Sidebar collapsible='icon'>
			<AppSidebarHeader />
			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<Access roles={['SuperUser']}>
							<CommandMenu />
						</Access>
						<SidebarMenu>
							{pricngItems.map((item) => {
								if (item.pathname && !item.items) {
									return (
										<SidebarMenuItem key={item.title}>
											<SidebarMenuButton
												data-testid={item.pathname}
												tooltip={item.title}
												asChild
											>
												<Link to={item.pathname}>
													{item.icon}
													<span>{item.title}</span>
												</Link>
											</SidebarMenuButton>
										</SidebarMenuItem>
									)
								}

								return (
									<Collapsible key={item.title} className='group/collapsible'>
										<SidebarMenuItem key={item.title}>
											{open ? (
												<CollapsibleTrigger asChild>
													<SidebarMenuButton
														tooltip={item.title}
														isActive={item.items?.some((item) =>
															location.pathname.includes(item.pathname)
														)}
													>
														{item.icon}
														<span className='truncate'>{item.title}</span>
														<ChevronRightIcon className='ml-auto group-data-[state=open]/collapsible:hidden' />
														<ChevronDownIcon className='ml-auto group-data-[state=closed]/collapsible:hidden' />
													</SidebarMenuButton>
												</CollapsibleTrigger>
											) : (
												<HoverCard openDelay={100} closeDelay={100}>
													<HoverCardTrigger asChild>
														<SidebarMenuButton
															isActive={item.items?.some((item) =>
																location.pathname.includes(item.pathname)
															)}
														>
															{item.icon}
															<span>{item.title}</span>
														</SidebarMenuButton>
													</HoverCardTrigger>
													<HoverCardContent
														side='right'
														align='start'
														className='w-auto p-0'
													>
														<div className='w-72 pb-3'>
															<div className='px-4 py-2 text-sm font-medium text-muted-foreground'>
																<span>{item.title}</span>
															</div>
															<SidebarMenuSub className='ml-5'>
																{item.items
																	?.filter((item) => item.enabled)
																	.map((item) => (
																		<SidebarMenuSubItem
																			className='group/menu-sub-item relative'
																			key={item.title}
																		>
																			<SidebarMenuSubButton
																				asChild
																				className='truncate'
																				data-testid={item.pathname}
																			>
																				<Link to={item.pathname}>
																					{item.title}
																				</Link>
																			</SidebarMenuSubButton>
																		</SidebarMenuSubItem>
																	))}
															</SidebarMenuSub>
														</div>
													</HoverCardContent>
												</HoverCard>
											)}

											{item.items?.length ? (
												<CollapsibleContent>
													<SidebarMenuSub>
														{item.items.map((item) => (
															<SidebarMenuSubItem
																className='group/menu-sub-item relative'
																key={item.title}
															>
																<SidebarMenuSubButton
																	asChild
																	className='truncate [&.active]:font-semibold'
																	data-testid={item.pathname}
																>
																	<Link to={item.pathname}>{item.title}</Link>
																</SidebarMenuSubButton>
															</SidebarMenuSubItem>
														))}
													</SidebarMenuSub>
												</CollapsibleContent>
											) : null}
										</SidebarMenuItem>
									</Collapsible>
								)
							})}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>

				<SidebarSeparator />
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							<FeatureFlag flagKey='fields_management' defaultValue={false}>
								<Access roles={['SuperUser']}>
									<SidebarMenuItem>
										<SidebarMenuButton
											data-testid='/p/data/field-schemas'
											tooltip={t('field_schema_page_title', {
												defaultValue: 'Field Schemas',
											})}
											asChild
										>
											<Link to='/p/data/field-schemas'>
												<DatabaseIcon />
												<span>
													{t('field_schema_page_title', {
														defaultValue: 'Field Schemas',
													})}
												</span>
											</Link>
										</SidebarMenuButton>
									</SidebarMenuItem>
								</Access>
							</FeatureFlag>
							<FeatureFlag flagKey='rule_builder' defaultValue={false}>
								<SidebarMenuItem>
									<SidebarMenuButton
										data-testid='/p/pricing-rules'
										tooltip={t('menu.pricing_rules', {
											defaultValue: 'Pricing rules',
										})}
										asChild
									>
										<Link to='/p/pricing-rules'>
											<RouteIcon />
											<span>
												{t('menu.pricing_rules', {
													defaultValue: 'Pricing rules',
												})}
											</span>
										</Link>
									</SidebarMenuButton>
								</SidebarMenuItem>
							</FeatureFlag>
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>

				<SidebarSeparator />

				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							<ExportsMenuItem />
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
				<SidebarGroup className='mt-auto'>
					<SidebarGroupContent>
						<SidebarMenu>
							<SidebarMenuItem>
								<SidebarMenuButton
									className='cursor-pointer'
									data-testid='contact-support-menu-item'
									tooltip={t('contact_support', {
										defaultValue: 'Contact support',
									})}
									onClick={() => showModal('CREATE_TICKET')}
								>
									<HeadsetIcon />
									<span>
										{t('contact_support', {
											defaultValue: 'Contact support',
										})}
									</span>
								</SidebarMenuButton>
							</SidebarMenuItem>
							<AssistantChat />
							<AppSidebarIntegrationMenuItem />
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
			<SidebarSeparator />
			<AppSidebarFooter />
			<SidebarRail />
		</Sidebar>
	)
}

function ExportsMenuItem() {
	const { t } = useTranslation()
	const { showModal } = useModals()
	const [newExports, setNewExports] = useLocalStorage(
		'new_exports_unreaded',
		false
	)
	const exportsQuery = useExportsQuery()

	return (
		<SidebarMenuItem>
			<SidebarMenuButton
				data-testid='exports-menu-item'
				tooltip={t('app.export_service', {
					defaultValue: 'Export service',
				})}
				className='cursor-pointer'
				onClick={() => {
					setNewExports(false)
					showModal('EXPORTS')
				}}
			>
				<DownloadIcon />
				{newExports && (
					<span className='absolute top-0.5 left-0.5 size-2 shrink-0 rounded-full bg-warning' />
				)}
				<span className='truncate'>
					{t('app.export_service', {
						defaultValue: 'Export service',
					})}
				</span>
			</SidebarMenuButton>
			<SidebarMenuBadge>{exportsQuery.data?.length}</SidebarMenuBadge>
		</SidebarMenuItem>
	)
}
